import { Route } from "@/types/route"

export const IS_PROD = import.meta.env.VITE_MODE === "prod"

// /dashboards 메뉴의 목적지 — 기존 Tableau 데이터포털 로그인
const TABLEAU_SIGNIN_URL = "https://tableau-dashboard.fitogether.com/#/signin"
// 자체 데이터포털(STRIDE). 비어 있으면 모든 팀이 Tableau 로 간다
const DATA_PORTAL_URL = import.meta.env.VITE_DATA_PORTAL_URL
// 파일럿 팀 목록(쉼표 구분 idfTeam). 있으면 이 팀들만 새 포털, 나머지는 Tableau.
// 비어 있으면(스테이지처럼) 포털 주소가 있는 모든 팀이 새 포털로 간다 — 태블로 종료 시 이 목록을 지우면 전 팀 전환
const DATA_PORTAL_PILOT_TEAMS = (import.meta.env.VITE_DATA_PORTAL_PILOT_TEAMS ?? "")
  .split(",")
  .map((s) => Number(s.trim()))
  .filter((n) => Number.isInteger(n) && n > 0)

// 파일럿 목록이 비어 있으면 모든 팀이 대상, 있으면 그 팀들만
export const isDataPortalPilotTeam = (idfTeam?: number): boolean =>
  DATA_PORTAL_PILOT_TEAMS.length === 0 ||
  (idfTeam !== undefined && DATA_PORTAL_PILOT_TEAMS.includes(idfTeam))

export const resolveDataPortalUrl = (idfTeam?: number): string => {
  if (!DATA_PORTAL_URL) {
    return TABLEAU_SIGNIN_URL
  }

  return isDataPortalPilotTeam(idfTeam) ? DATA_PORTAL_URL : TABLEAU_SIGNIN_URL
}

export const APP_ROUTES: Route[] = [
  {
    path: "/noTeam",
    name: "noTeam",
    disableTreeView: true,
  },
  {
    path: "/home",
    name: "home",
    icon: "home",
  },
  {
    path: "/activity",
    name: "activity",
    redirect: "/activity/list",
    icon: "bar-chart-horizontal-fill",
    auth: ["C"],
    children: [
      {
        path: "/activity/list",
        name: "activityList",
        disableBreadcrumbs: true,
        disableTreeView: true,
      },

      {
        path: "/activity/detail",
        name: "activityDetails",
        disableTreeView: true,
      },
    ],
  },
  {
    path: "/report",
    name: "report",
    icon: "file-chart",
    children: [
      {
        path: "/report/daily",
        name: "dailyReport",
      },
      {
        path: "/report/trend",
        name: "trendReport",
      },
      {
        path: "/report/ai-report",
        name: "aiReport",
        permission: "ACCESS_AI_REPORT",
      },
      // {
      //   path: "/report/report",
      //   name: "report",
      //   deprecated: true,
      // },
    ],
  },
  {
    path: "/export",
    name: "export",
    icon: "file-download",
    redirect: "/export/pdf",
    auth: ["C"],
    children: [
      {
        path: "/export/pdf",
        name: "pdfReportEditor",
      },
      {
        path: "/export/csv",
        name: "csvExport",
      },
    ],
  },
  {
    path: "/dashboards",
    name: "dashboards",
    icon: "line-chart",
    auth: ["C"],
    // 데이터포털 접근 권한이 있는 팀에만 노출 (team_permission.access_tableau)
    permission: "ACCESS_TABLEAU",
    // 자체 데이터포털(STRIDE) 전환 중 — 팀별로 새 포털/기존 Tableau 를 가른다 (resolveDataPortalUrl 참조)
    externalUrl: resolveDataPortalUrl(),
    externalUrlForTeam: resolveDataPortalUrl,
    // 파일럿 목록이 있으면 그 팀에게만 메뉴를 보인다 — 나머지 팀은 Tableau 링크도 노출하지 않는다
    visibleForTeam: isDataPortalPilotTeam,
  },
  {
    path: "/settings",
    name: "settings",
    redirect: "overview",
    icon: "settings",
    auth: ["C"],
    children: [
      {
        path: "/settings/overview",
        name: "overview",
      },
      {
        path: "/settings/metric",
        name: "metricKey",
        redirect: "zones",
        children: [
          {
            path: "/settings/metric/zones",
            name: "zones",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/metric/actions",
            name: "actions",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/metric/advanced",
            name: "advanced",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/metric/calculator",
            name: "customMetricCalculator",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
        ],
      },
      {
        path: "/settings/analysis-groups",
        name: "analysisGroups",
        redirect: "players",
        children: [
          {
            path: "/settings/analysis-groups/players",
            name: "players",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/analysis-groups/sessions",
            name: "sessions",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/analysis-groups/season",
            name: "season",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
        ],
      },
      {
        path: "/settings/report",
        name: "report",
        redirect: "report",
        children: [
          {
            path: "/settings/report/daily",
            name: "dailyReport",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/report/trend",
            name: "trendReport",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/report/filter-preset",
            name: "filterPreset",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          // {
          //   path: "/settings/report/report",
          //   name: "report",
          //   deprecated: true,
          //   props: {
          //     marker: "arrow-drop-right-fill",
          //   },
          // },
        ],
      },
      {
        path: "/settings/club",
        name: "club",
        redirect: "coaches",
        children: [
          {
            path: "/settings/club/coaches",
            name: "coaches",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/club/players",
            name: "players",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/club/transition",
            name: "transition",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/club/equipments",
            name: "equipments",
            props: {
              marker: "arrow-drop-right-fill",
            },
            children: [
              {
                path: "/settings/club/equipments/delivery",
                name: "delivery",
                auth: ["C"],
                disableTreeView: true,
              },
            ],
          },
        ],
      },
      {
        path: "/settings/preferences",
        name: "preferences",
        redirect: "language",
        children: [
          {
            path: "/settings/preferences/languages",
            name: "languages",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
          {
            path: "/settings/preferences/units",
            name: "units",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
        ],
      },
      {
        path: "/settings/resources",
        name: "resources",
        redirect: "download",
        children: [
          {
            path: "/settings/resources/download",
            name: "swDownload",
            props: {
              marker: "arrow-drop-right-fill",
            },
          },
        ],
      },
    ],
  },
  {
    path: "/account",
    name: "account",
    disableTreeView: true,
  },
  {
    path: "/notice",
    name: "notice",
    disableTreeView: true,
    children: [
      {
        path: "/notice/:idfNotice",
        name: "noticeDetails",
        auth: ["P", "C"],
      },
    ],
  },
]
